import type {
    FilterBuilderAddFilterGroup,
    FilterBuilderAddFilterOption,
} from "components/ApplicationFilters/FiltersAndChips/BaseFilterComponents/FilterBuilderAddFilterSelect";
import { useCallback, useMemo } from "react";
import { ApplicationFilterItemsStore } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { AuthStore } from "stores/AuthStore";
import type { StatusDescriptor } from "components/UI/StatusTags";
import { useStore } from "utils/useStore";
import {
    isGroupByOption,
    SignalsGroupByOptionLabels,
    type GroupByOptionValue,
} from "../../../SignalsModels";
import ChartCardStore from "../../ChartCard/ChartCardStore";
import { GROUP_BY_OPTION_CONFIG } from "./groupByOptionConfig";
import { useGroupableExtendedMetadataFields } from "./useGroupableExtendedMetadataFields";

export type GroupBySelectOption = {
    value: GroupByOptionValue;
    label: string;
    isDisabled: boolean;
    disabledLabel?: string;
};

export type GroupByMenuOption = GroupBySelectOption &
    FilterBuilderAddFilterOption & {
        selectValue: string;
        itemName: string;
        valueLabel: string;
        extendedMetadataKey?: string;
    };

export type GroupByMenuGroup = FilterBuilderAddFilterGroup<GroupByMenuOption>;

/**
 * Builds the Group By menu from the backend catalog. Options that map to a filter-picker entry
 * inherit its group, position, title, and status tag, so the menu is laid out the way the filter
 * picker is; the rest fall back to the group named in GROUP_BY_OPTION_CONFIG.
 *
 * The catalog decides what *exists* -- an option the backend does not publish never reaches this
 * menu -- and `canUseGroupByOption` decides what this viewer may see of it.
 */
export const useGroupByMenuOptions = (chartStore: ChartCardStore) => {
    const applicationFilterItemsStore = useStore(ApplicationFilterItemsStore);
    const authStore = useStore(AuthStore);

    /**
     * A group-by label marked "(Deprecated)" where the surface renders a plain string and cannot
     * carry a status tag -- the Orient-Chart-By toggle and the value/item names.
     *
     * Only viewers who can see the filter's Deprecated status get the marker; the rest see one
     * Theme option with nothing to distinguish it from. The menu titles themselves are unmarked
     * because they render a real tag. See ApplicationFilterItemsStore.withDeprecationMarker.
     */
    const markGroupByLabel = useCallback(
        (option: GroupByOptionValue | undefined, label: string) =>
            applicationFilterItemsStore.withDeprecationMarker(
                option ? GROUP_BY_OPTION_CONFIG[option].filterKey : undefined,
                label,
            ),
        [applicationFilterItemsStore],
    );

    const groupableExtendedMetadataFields =
        useGroupableExtendedMetadataFields();

    /**
     * Whether the viewer may group by this dimension at all, as opposed to whether this chart
     * offers it.
     *
     * Two rules, because a dimension can be gated two ways and neither subsumes the other.
     *
     * The catalog's `requiredViewPermissions` is the backend's own answer, read here rather than
     * restated, and matched to how the backend applies it: a dimension listing several is visible if
     * the viewer has any one of them, so it hides only when they have none. Without this the field
     * is published and nothing reads it, and a dimension that reaches the menu through the fallback
     * group gets no permission check at all.
     *
     * `canAccessFilterItem` is the second, for dimensions that take a filter's rules. It is the
     * only place that knows the inverted ones -- Restrict Evaluations hides module scores, question
     * results and evaluation types from non-ultra users, which no list of required permissions can
     * express. The filter-derived path below checks the same item again; this is what keeps an
     * option out of the select list that feeds the rest of the configuration UI.
     */
    const canUseGroupByOption = useCallback(
        (option: GroupByOptionValue): boolean => {
            const requiredViewPermissions =
                chartStore.optionsStore.signalsGroupByCatalog.find(
                    (item) => item.groupByOption === option,
                )?.requiredViewPermissions ?? [];

            if (
                requiredViewPermissions.length > 0 &&
                !requiredViewPermissions.some((permission) =>
                    authStore.canUserView(permission),
                )
            ) {
                return false;
            }

            const { filterKey } = GROUP_BY_OPTION_CONFIG[option];
            if (!filterKey) return true;

            const item = applicationFilterItemsStore.getFilterItem(filterKey);
            return !item || applicationFilterItemsStore.canAccessFilterItem(item);
        },
        [
            applicationFilterItemsStore,
            authStore,
            chartStore.optionsStore.signalsGroupByCatalog,
        ],
    );

    const availableOptions = useMemo((): GroupBySelectOption[] => {
        const disabledGroupByNames =
            chartStore.config.grouping.disabledGroupByNames ?? [];

        return chartStore.optionsStore.signalsGroupByCatalog
            .map((item) => item.groupByOption)
            .filter(isGroupByOption)
            .filter(canUseGroupByOption)
            .map((option) => ({
                value: option,
                label: markGroupByLabel(
                    option,
                    SignalsGroupByOptionLabels[option],
                ),
                isDisabled: disabledGroupByNames.includes(option),
                disabledLabel: disabledGroupByNames.includes(option)
                    ? "Not available on this chart"
                    : undefined,
            }));
    }, [
        canUseGroupByOption,
        chartStore.config.grouping.disabledGroupByNames,
        chartStore.optionsStore.signalsGroupByCatalog,
        markGroupByLabel,
    ]);

    const groupBySelectGroups = useMemo((): GroupByMenuGroup[] => {
        const buildMenuOption = (
            option: GroupBySelectOption,
            title: string,
            overrides?: Partial<
                Pick<
                    GroupByMenuOption,
                    | "selectValue"
                    | "itemName"
                    | "valueLabel"
                    | "extendedMetadataKey"
                >
            >,
            status?: StatusDescriptor,
        ): GroupByMenuOption => {
            const optionConfig = GROUP_BY_OPTION_CONFIG[option.value];

            return {
                ...option,
                title,
                status,
                selectValue: overrides?.selectValue ?? option.value,
                itemName:
                    overrides?.itemName ??
                    markGroupByLabel(option.value, optionConfig.itemName),
                valueLabel:
                    overrides?.valueLabel ??
                    markGroupByLabel(option.value, optionConfig.valueLabel),
                extendedMetadataKey: overrides?.extendedMetadataKey,
            };
        };

        const groupsByLabel = new Map<string, GroupByMenuOption[]>();
        const usedOptions = new Set<GroupByOptionValue>();
        const groupableExtendedMetadataKeys = new Set(
            groupableExtendedMetadataFields.map((field) => field.name),
        );

        // Legacy bucketing, matching the filter picker and chart object filters.
        const groupByFilterGroups =
            applicationFilterItemsStore.withLegacyGrouping(
                applicationFilterItemsStore.filterGroups,
            );
        groupByFilterGroups.forEach((group) => {
            group.items.forEach((item) => {
                if (!applicationFilterItemsStore.canAccessFilterItem(item)) {
                    return;
                }

                let matchingOption = availableOptions.find(
                    (option) =>
                        GROUP_BY_OPTION_CONFIG[option.value].filterKey ===
                        item.filterKey,
                );
                const extendedMetadataKey = item.filterKey.startsWith(
                    "extendedMetadata.",
                )
                    ? item.filterKey.replace("extendedMetadata.", "")
                    : undefined;

                if (
                    !matchingOption &&
                    extendedMetadataKey &&
                    groupableExtendedMetadataKeys.has(extendedMetadataKey)
                ) {
                    matchingOption = availableOptions.find(
                        (option) => option.value === "ExtendedMetadata",
                    );
                }

                if (!matchingOption) {
                    return;
                }

                usedOptions.add(matchingOption.value);
                const nextGroupOptions = groupsByLabel.get(group.title) ?? [];

                nextGroupOptions.push(
                    buildMenuOption(
                        matchingOption,
                        GROUP_BY_OPTION_CONFIG[matchingOption.value].menuTitle ??
                            item.title,
                        extendedMetadataKey
                            ? {
                                  selectValue: item.filterKey,
                                  itemName: "Extended Metadata Value",
                                  valueLabel: `${item.title} Values`,
                                  extendedMetadataKey,
                              }
                            : undefined,
                        // Filter-derived options inherit the filter's status, so the group-by
                        // menu tells the two Theme options apart the same way the picker does.
                        item.status,
                    ),
                );

                groupsByLabel.set(group.title, nextGroupOptions);
            });
        });

        // The safety net for a dimension with no filter entry to sit beside. Every dimension the
        // catalog publishes today has one, so nothing lands here in practice -- it exists so that a
        // new one still reaches the menu, under a heading of its own, instead of disappearing.
        availableOptions.forEach((option) => {
            if (usedOptions.has(option.value)) {
                return;
            }

            const optionConfig = GROUP_BY_OPTION_CONFIG[option.value];
            if (optionConfig.filterKey || option.value === "ExtendedMetadata") {
                return;
            }

            const fallbackGroupLabel =
                optionConfig.fallbackGroupLabel ?? "Additional Groupings";
            const nextGroupOptions = groupsByLabel.get(fallbackGroupLabel) ?? [];

            nextGroupOptions.push(buildMenuOption(option, option.label));
            groupsByLabel.set(fallbackGroupLabel, nextGroupOptions);
        });

        const orderedGroupLabels = [
            ...groupByFilterGroups.map((group) => group.title),
            ...Array.from(groupsByLabel.keys()).filter(
                (label) =>
                    !groupByFilterGroups.some((group) => group.title === label),
            ),
        ];

        return orderedGroupLabels
            .map((label) => ({
                label,
                options: groupsByLabel.get(label) ?? [],
            }))
            .filter((group) => group.options.length > 0);
    }, [
        applicationFilterItemsStore,
        availableOptions,
        groupableExtendedMetadataFields,
        markGroupByLabel,
    ]);

    return {
        groupBySelectGroups,
        markGroupByLabel,
    };
};
